import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { cartAPI, productsAPI } from '../lib/api';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const CartContext = createContext(null);

// Analytics event tracking - we can't use the context here since CartProvider is a sibling
// So we'll track events directly via window events
const trackAnalyticsEvent = (eventType, data) => {
    window.dispatchEvent(new CustomEvent('track-analytics', { 
        detail: { eventType, ...data } 
    }));
};

export function CartProvider({ children }) {
    const { isAuthenticated } = useAuth();
    const [cart, setCart] = useState({ items: [], total: 0 });
    const [loading, setLoading] = useState(false);
    const [itemCount, setItemCount] = useState(0);

    const fetchCart = useCallback(async () => {
        if (!isAuthenticated) {
            setCart({ items: [], total: 0 });
            setItemCount(0);
            return;
        }
        
        try {
            setLoading(true);
            const response = await cartAPI.get();
            setCart(response.data);
            setItemCount(response.data.items?.reduce((sum, item) => sum + item.quantity, 0) || 0);
        } catch (error) {
            console.error('Failed to fetch cart:', error);
        } finally {
            setLoading(false);
        }
    }, [isAuthenticated]);

    useEffect(() => {
        fetchCart();
    }, [fetchCart]);

    const addToCart = async (productId, quantity = 1, showToast = true) => {
        if (!isAuthenticated) {
            toast.error('Kérlek jelentkezz be a kosárhoz adáshoz');
            return false;
        }
        
        try {
            await cartAPI.add(productId, quantity);
            await fetchCart();
            
            // Track add to cart event
            try {
                const product = await productsAPI.getById(productId);
                if (product?.data) {
                    trackAnalyticsEvent('add_to_cart', { 
                        product_id: productId, 
                        value: product.data.price * quantity,
                        category: product.data.subcategory || product.data.category
                    });
                }
            } catch (e) {
                // Silently fail analytics
            }
            
            if (showToast) {
                toast.success('Hozzáadva a kosárhoz');
            }
            return true;
        } catch (error) {
            console.error('Failed to add to cart:', error);
            if (showToast) {
                toast.error('Hiba történt a kosárhoz adáskor');
            }
            return false;
        }
    };

    const addMultipleToCart = async (items) => {
        // items is an array of { productId, quantity } objects
        if (!isAuthenticated) {
            toast.error('Kérlek jelentkezz be a kosárhoz adáshoz');
            return false;
        }
        
        if (!items || items.length === 0) {
            return false;
        }
        
        try {
            // Add all items without individual toasts
            let successCount = 0;
            let failCount = 0;
            
            for (const item of items) {
                try {
                    await cartAPI.add(item.productId, item.quantity || 1);
                    successCount++;
                } catch (error) {
                    console.error(`Failed to add product ${item.productId}:`, error);
                    failCount++;
                }
            }
            
            // Refresh cart once after all items are added
            await fetchCart();
            
            // Show a single merged notification
            if (failCount === 0) {
                toast.success(`${successCount} alkatrész hozzáadva a kosárhoz`);
            } else if (successCount > 0) {
                toast.warning(`${successCount} alkatrész hozzáadva, ${failCount} hiba történt`);
            } else {
                toast.error('Hiba történt a kosárhoz adáskor');
            }
            
            return successCount > 0;
        } catch (error) {
            console.error('Failed to add multiple items to cart:', error);
            toast.error('Hiba történt a kosárhoz adáskor');
            return false;
        }
    };

    const updateQuantity = async (productId, quantity) => {
        try {
            if (quantity <= 0) {
                await removeFromCart(productId);
            } else {
                await cartAPI.update(productId, quantity);
                await fetchCart();
            }
        } catch (error) {
            console.error('Failed to update cart:', error);
            toast.error('Hiba történt a kosár frissítésekor');
        }
    };

    const removeFromCart = async (productId) => {
        try {
            await cartAPI.remove(productId);
            await fetchCart();
            
            // Track remove from cart event
            trackAnalyticsEvent('remove_from_cart', { product_id: productId });
            
            toast.success('Eltávolítva a kosárból');
        } catch (error) {
            console.error('Failed to remove from cart:', error);
            toast.error('Hiba történt az eltávolításkor');
        }
    };

    const clearCart = async () => {
        try {
            await cartAPI.clear();
            await fetchCart();
            toast.success('Kosár kiürítve');
        } catch (error) {
            console.error('Failed to clear cart:', error);
            toast.error('Hiba történt a kosár kiürítésekor');
        }
    };

    return (
        <CartContext.Provider value={{
            cart,
            loading,
            itemCount,
            addToCart,
            addMultipleToCart,
            updateQuantity,
            removeFromCart,
            clearCart,
            refreshCart: fetchCart,
        }}>
            {children}
        </CartContext.Provider>
    );
}

export function useCart() {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
}
